<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';

const props = defineProps({
  task: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['view']);

const { t } = useI18n();

// Récupérer les agents de l'espace
const agents = useMapGetter('agents/getVerifiedAgents');

const isHovered = ref(false);

// Couleur du badge de priorité avec design amélioré
const priorityConfig = computed(() => {
  switch (props.task.priority) {
    case 'high':
      return {
        bg: 'bg-red-100 dark:bg-red-900/30',
        text: 'text-red-700 dark:text-red-300',
        icon: 'i-lucide-chevrons-up',
        ring: 'ring-red-100/50 dark:ring-red-900/50',
      };
    case 'medium':
      return {
        bg: 'bg-woot-500',
        text: 'text-white',
        icon: 'i-lucide-chevron-up',
        ring: 'ring-woot-500/20',
      };
    case 'low':
      return {
        bg: 'bg-green-100 dark:bg-green-900/30',
        text: 'text-green-700 dark:text-green-300',
        icon: 'i-lucide-chevron-down',
        ring: 'ring-green-100/50 dark:ring-green-900/50',
      };
    default:
      return {
        bg: 'bg-slate-100 dark:bg-slate-800',
        text: 'text-slate-700 dark:text-slate-300',
        icon: 'i-lucide-minus',
        ring: 'ring-slate-100/50 dark:ring-slate-800/50',
      };
  }
});

// Formater la date de manière plus élégante
const formatDate = dateString => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const isToday = date.toDateString() === today.toDateString();
  const isTomorrow = date.toDateString() === tomorrow.toDateString();

  if (isToday) return "Aujourd'hui";
  if (isTomorrow) return 'Demain';

  return date.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
  });
};

// Vérifier si la date est dépassée
const isOverdue = computed(() => {
  if (!props.task.dueDate) return false;
  return new Date(props.task.dueDate) < new Date();
});

// Agents assignés à la tâche
const assignedAgents = computed(() => {
  if (!props.task.assignees || props.task.assignees.length === 0) return [];
  return props.task.assignees
    .map(agentId => agents.value.find(a => a.id === agentId))
    .filter(Boolean);
});

// Initiales d'un agent
const getAgentInitials = agent => {
  if (!agent?.name) return '?';
  return agent.name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

// Couleur de l'avatar basée sur l'ID de l'agent
const getAvatarColorClass = agentId => {
  const colors = [
    'bg-woot-500',
    'bg-violet-300',
    'bg-green-300',
    'bg-yellow-200',
    'bg-red-300',
    'bg-slate-300',
  ];
  return colors[agentId % colors.length];
};

// Couleur du texte de l'avatar
const getAvatarTextClass = colorClass => {
  if (colorClass.includes('300') || colorClass.includes('200')) {
    return 'text-slate-800';
  }
  return 'text-white';
};

// Vérifier si la description existe
const hasDescription = computed(() => {
  if (!props.task.description) return false;
  const temp = document.createElement('div');
  temp.innerHTML = props.task.description;
  const text = temp.textContent || temp.innerText || '';
  return text.trim().length > 0;
});
</script>

<template>
  <div
    class="group relative rounded-xl border-2 bg-gradient-to-br from-white to-[#F9F9F9] dark:from-[#1D1D1F] dark:to-[#0F0F10] transition-all duration-200 cursor-pointer select-none"
    :class="{
      'border-[#E5E7EB] dark:border-[#2A2A2C] hover:border-[#3860F9]/40 dark:hover:border-[#D3FD58]/40 hover:shadow-lg hover:shadow-[#3860F9]/5 dark:hover:shadow-[#D3FD58]/5 hover:-translate-y-0.5':
        !isHovered,
      'border-[#3860F9]/60 dark:border-[#D3FD58]/60 shadow-xl shadow-[#3860F9]/10 dark:shadow-[#D3FD58]/10 -translate-y-1':
        isHovered,
    }"
    @mouseenter="isHovered = true"
    @mouseleave="isHovered = false"
    @click="emit('view')"
  >
    <!-- Badge de priorité en haut à gauche -->
    <div class="absolute -top-2 -left-2 z-10">
      <div
        :class="[priorityConfig.bg, priorityConfig.text, priorityConfig.ring]"
        class="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide shadow-lg ring-4 ring-white dark:ring-[#1D1D1F]"
      >
        <span :class="priorityConfig.icon" class="size-3" />
        <span>{{ t(`TASKS.PRIORITY.${task.priority.toUpperCase()}`) }}</span>
      </div>
    </div>

    <!-- ID en haut à droite -->
    <div class="absolute top-2 right-2 z-10">
      <span
        class="font-mono text-[10px] font-medium text-[#6B7280] dark:text-[#9CA3AF] bg-white/90 dark:bg-[#1D1D1F]/90 backdrop-blur-sm px-2 py-0.5 rounded-md border border-[#E5E7EB] dark:border-[#2A2A2C] shadow-sm"
      >
        {{ `#${task.id}` }}
      </span>
    </div>

    <!-- Contenu de la carte -->
    <div class="p-4 pt-5">
      <!-- Titre -->
      <h4
        class="font-semibold text-sm text-[#1D1D1F] dark:text-white line-clamp-2 leading-relaxed"
        :class="{
          'mb-2': task.tags && task.tags.length > 0,
          'mb-3': !task.tags || task.tags.length === 0,
        }"
      >
        {{ task.title }}
      </h4>

      <!-- Tags -->
      <div
        v-if="task.tags && task.tags.length > 0"
        class="flex flex-wrap gap-1.5 mb-3"
      >
        <span
          v-for="(tag, index) in task.tags.slice(0, 3)"
          :key="index"
          class="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-[#3860F9]/10 dark:bg-[#D3FD58]/10 text-[#3860F9] dark:text-[#D3FD58] border border-[#3860F9]/20 dark:border-[#D3FD58]/20"
        >
          {{ tag }}
        </span>
        <span
          v-if="task.tags.length > 3"
          class="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-[#F9FAFB] dark:bg-[#1D1D1F] text-[#6B7280] dark:text-[#9CA3AF] border border-[#E5E7EB] dark:border-[#2A2A2C]"
        >
          +{{ task.tags.length - 3 }}
        </span>
      </div>

      <!-- Description (si présente) -->
      <div
        v-if="hasDescription"
        class="text-xs text-[#6B7280] dark:text-[#9CA3AF] line-clamp-2 leading-relaxed mb-3 rich-text-preview"
        v-html="task.description"
      />

      <!-- Footer avec assignés et date -->
      <div
        class="flex items-center justify-between gap-2 mt-4 pt-3 border-t border-[#E5E7EB]/50 dark:border-[#2A2A2C]/50"
      >
        <!-- Assignés (avatar + nom) -->
        <div class="flex items-center gap-2 min-w-0 flex-1">
          <div
            v-if="assignedAgents.length > 0"
            class="flex items-center gap-2 min-w-0"
          >
            <!-- Premier agent avec avatar et nom -->
            <div
              v-if="assignedAgents[0]"
              class="flex items-center gap-1.5 min-w-0"
            >
              <div
                v-if="
                  assignedAgents[0].thumbnail || assignedAgents[0].avatar_url
                "
                class="size-6 rounded-full overflow-hidden ring-2 ring-white dark:ring-[#1D1D1F] shadow-sm flex-shrink-0"
              >
                <img
                  :src="
                    assignedAgents[0].thumbnail || assignedAgents[0].avatar_url
                  "
                  :alt="assignedAgents[0].name"
                  class="w-full h-full object-cover"
                />
              </div>
              <div
                v-else
                :class="[
                  getAvatarColorClass(assignedAgents[0].id),
                  getAvatarTextClass(getAvatarColorClass(assignedAgents[0].id)),
                ]"
                class="size-6 rounded-full flex items-center justify-center text-[10px] font-bold ring-2 ring-white dark:ring-[#1D1D1F] shadow-sm flex-shrink-0"
              >
                {{ getAgentInitials(assignedAgents[0]) }}
              </div>
              <span
                class="text-xs text-[#6B7280] dark:text-[#9CA3AF] font-medium truncate"
              >
                {{ assignedAgents[0].name }}
              </span>
            </div>

            <!-- Avatars supplémentaires (si plus d'un agent) -->
            <div
              v-if="assignedAgents.length > 1"
              class="flex items-center -space-x-2 ml-1"
            >
              <div
                v-for="agent in assignedAgents.slice(1, MAX_VISIBLE_AVATARS)"
                :key="agent.id"
                class="relative"
              >
                <div
                  v-if="agent.thumbnail || agent.avatar_url"
                  class="size-5 rounded-full overflow-hidden ring-2 ring-white dark:ring-[#1D1D1F] shadow-sm flex-shrink-0 transition-transform hover:scale-125 hover:z-10"
                  :title="agent.name"
                >
                  <img
                    :src="agent.thumbnail || agent.avatar_url"
                    :alt="agent.name"
                    class="w-full h-full object-cover"
                  />
                </div>
                <div
                  v-else
                  :class="[
                    getAvatarColorClass(agent.id),
                    getAvatarTextClass(getAvatarColorClass(agent.id)),
                  ]"
                  class="size-5 rounded-full flex items-center justify-center text-[9px] font-bold ring-2 ring-white dark:ring-[#1D1D1F] shadow-sm flex-shrink-0 transition-transform hover:scale-125 hover:z-10"
                  :title="agent.name"
                >
                  {{ getAgentInitials(agent) }}
                </div>
              </div>
              <!-- Badge de compteur si plus de 3 agents -->
              <div
                v-if="remainingCount > 0"
                class="size-5 rounded-full bg-[#F9FAFB] dark:bg-[#1D1D1F] text-[#6B7280] dark:text-[#9CA3AF] flex items-center justify-center text-[9px] font-bold ring-2 ring-white dark:ring-[#1D1D1F] shadow-sm flex-shrink-0"
                :title="`+${remainingCount} ${t('TASKS.MORE_ASSIGNEES')}`"
              >
                +{{ remainingCount }}
              </div>
            </div>
          </div>
        </div>

        <!-- Date d'échéance -->
        <div v-if="task.dueDate" class="flex items-center gap-1 flex-shrink-0">
          <span
            class="i-lucide-calendar-clock size-3"
            :class="{
              'text-red-600 dark:text-red-400': isOverdue,
              'text-[#6B7280] dark:text-[#9CA3AF]': !isOverdue,
            }"
          />
          <time
            class="text-[11px] font-medium tabular-nums whitespace-nowrap"
            :class="{
              'text-red-600 dark:text-red-400': isOverdue,
              'text-[#6B7280] dark:text-[#9CA3AF]': !isOverdue,
            }"
          >
            {{ formatDate(task.dueDate) }}
          </time>
        </div>
      </div>

      <!-- Fichiers et URL -->
      <div
        v-if="(task.files && task.files.length > 0) || task.url"
        class="mt-3 pt-3 border-t border-[#E5E7EB]/50 dark:border-[#2A2A2C]/50 space-y-2"
      >
        <!-- Fichiers -->
        <div
          v-if="task.files && task.files.length > 0"
          class="flex items-center gap-2"
        >
          <span
            class="i-lucide-paperclip size-3 text-[#6B7280] dark:text-[#9CA3AF] flex-shrink-0"
          />
          <span class="text-xs text-[#6B7280] dark:text-[#9CA3AF]">
            {{ task.files.length }}
            {{ task.files.length === 1 ? 'fichier' : 'fichiers' }}
          </span>
        </div>

        <!-- URL -->
        <div v-if="task.url" class="flex items-center gap-2 min-w-0">
          <span
            class="i-lucide-link size-3 text-[#6B7280] dark:text-[#9CA3AF] flex-shrink-0"
          />
          <a
            :href="task.url"
            target="_blank"
            rel="noopener noreferrer"
            class="text-xs text-woot-500 hover:underline truncate"
            @click.stop
          >
            {{ task.url }}
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Styles pour le contenu riche dans l'aperçu */
.rich-text-preview :deep(b),
.rich-text-preview :deep(strong) {
  font-weight: 700;
}

.rich-text-preview :deep(i),
.rich-text-preview :deep(em) {
  font-style: italic;
}

.rich-text-preview :deep(u) {
  text-decoration: underline;
}

.rich-text-preview :deep(s),
.rich-text-preview :deep(strike) {
  text-decoration: line-through;
}

.rich-text-preview :deep(h1),
.rich-text-preview :deep(h2),
.rich-text-preview :deep(h3) {
  font-weight: 600;
  font-size: inherit;
}

.rich-text-preview :deep(blockquote) {
  border-left: 2px solid currentColor;
  padding-left: 0.5em;
  opacity: 0.8;
}

.rich-text-preview :deep(mark) {
  background-color: #fef08a;
  padding: 0 0.1em;
  border-radius: 2px;
}

.rich-text-preview :deep(a) {
  color: #3b82f6;
  text-decoration: underline;
}

.rich-text-preview :deep(p) {
  display: inline;
}

.rich-text-preview :deep(ul),
.rich-text-preview :deep(ol) {
  padding-left: 1em;
  display: inline;
}

.rich-text-preview :deep(li) {
  display: inline;
}

.rich-text-preview :deep(li::after) {
  content: ', ';
}

.rich-text-preview :deep(li:last-child::after) {
  content: '';
}
</style>
